import { useRef, useEffect } from 'react';
import { useMutation, useQueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import DocumentViewer from './DocumentViewer';
import { updateDocumentDetails } from '../../../../api/v2/load';

const useDocumentViewer = ({ refetchDocuments, onDocumentSaved }) => {
  const channelRef = useRef(null);
  const openWindowsRef = useRef({});
  const lockReleasesRef = useRef({});
  const queryClient = useQueryClient();

  // When component unmounts or the window is about to unload
  // 1) clean up any open BroadcastChannels and release any locks
  // 2) removes any queries related to 'document' from the query client
  useEffect(() => {
    const cleanUp = () => {
      if (channelRef.current) {
        channelRef.current.close();
      }
      Object.values(lockReleasesRef.current).forEach((release) => release());
      lockReleasesRef.current = {};
    };
    window.addEventListener('beforeunload', cleanUp);

    return () => {
      cleanUp();
      window.removeEventListener('beforeunload', cleanUp);
      queryClient.removeQueries({ queryKey: ['document'] });
    };
  }, []);

  // Mutation to update document details
  const updateDocumentMutation = useMutation({
    mutationFn: updateDocumentDetails,
    onSuccess: async (_, variables) => {
      const refreshedDocuments = await refetchDocuments();

      onDocumentSaved?.({
        id: variables.id,
        ...(variables.invoiceNumber && { invoiceNumber: variables.invoiceNumber }),
        documents: refreshedDocuments.data || [],
      });
    },
  });

  // Clones a <link>/<style> node into the child document and resolves once its CSS is actually
  // usable there. Cloning a <link> does not carry over the parent's loaded state - the child
  // document has to (re)load it, so rendering before this resolves can cause a flash of
  // unstyled content (most noticeable in Firefox, and only observed with production <link>
  // stylesheets rather than the <style> tags CRA's dev server injects).
  const cloneStylesheetAndWait = async (node, targetDoc, timeoutMs = 3000) => {
    const clone = node.cloneNode(true);
    targetDoc.head.appendChild(clone);

    // Only <link> stylesheets need waiting for; <style> tags and already-cached sheets are ready immediately
    if (clone.tagName !== 'LINK' || clone.sheet) {
      return;
    }

    await new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        resolve();
      };
      clone.addEventListener('load', done, { once: true });
      clone.addEventListener('error', done, { once: true });
      const timer = setTimeout(done, timeoutMs);
    });
  };

  // Function to open a new window for the document viewer
  const openDocumentViewerWindow = async (title, features = 'width=1400,height=768') => {
    const win = window.open('', '_blank', features);

    if (!win) {
      alert('Failed to open new window - please ensure that pop-ups are not blocked.');
      return null;
    }

    win.document.title = title;
    win.document.body.style.margin = 0;
    // Hide content until stylesheets are ready to avoid a flash of unstyled content
    win.document.body.style.visibility = 'hidden';

    // Carry over the parent's stylesheets (MUI theme, Bootstrap, App.css, etc.)
    const nodes = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'));
    await Promise.all(nodes.map((node) => cloneStylesheetAndWait(node, win.document)));

    const mountEl = win.document.createElement('div');
    win.document.body.appendChild(mountEl);
    win.document.body.style.visibility = 'visible';

    return { win, mountEl };
  };

  const disconnectDocumentViewer = (sessionId) => {
    const release = lockReleasesRef.current[sessionId];
    if (!release) return;

    release();
    delete lockReleasesRef.current[sessionId];
  };

  // Function to open the document viewer with a specific session ID
  const openDocumentViewer = (sessionId, portalRef, documentId, filename, documentsList) => {
    // If a window is already open, then send a message to change the document and bring it to focus
    const existingWin = openWindowsRef.current[sessionId];
    if (existingWin && !existingWin.closed) {
      if (lockReleasesRef.current[sessionId] && channelRef.current) {
        channelRef.current.postMessage({ type: 'select-document', payload: { documentId, filename } });
      }

      existingWin.focus();
      return;
    }

    // If a window is not already open, then open one and render the document viewer
    const renderDocumentViewer = async () => {
      const result = await openDocumentViewerWindow(`Document Viewer - ${sessionId}`);
      if (!result) {
        return;
      }
      const { win, mountEl } = result;

      openWindowsRef.current[sessionId] = win;

      const channelName = `document-session-${sessionId}`;
      const channel = new BroadcastChannel(channelName);
      channelRef.current = channel;

      // Hold exclusive lock; auto-released by browser on tab close
      const lockName = `lock-${channelName}`;
      const lockPromise = new Promise((resolve) => {
        lockReleasesRef.current[sessionId] = resolve;
      });
      navigator.locks.request(lockName, { mode: 'exclusive' }, () => lockPromise);

      channel.onmessage = (event) => {
        const { type, payload } = event.data;

        // Cleanup if the child window is closed
        if (type === 'child-closed') {
          channel.close();
          channelRef.current = null;
          delete openWindowsRef.current[sessionId];
          if (lockReleasesRef.current[sessionId]) {
            lockReleasesRef.current[sessionId]();
            delete lockReleasesRef.current[sessionId];
          }
        }

        // Received message from child window to update document details
        if (type === 'update-document') {
          const { id, invoiceNumber, documentType, cm8 } = payload;
          updateDocumentMutation.mutate(
            { id, invoiceNumber, documentType, cm8 },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['document', id] });
                channel.postMessage({ type: 'document-saved' });
              },
              onError: (error) => {
                console.error('Failed to update document details:', error);
                channel.postMessage({ type: 'document-save-error', payload: { error: error.message } });
              },
            }
          );
        }
      };

      const { createRoot } = await import('react-dom/client');
      const root = createRoot(mountEl);
      const emotionCache = createCache({ key: 'document-viewer', container: win.document.head });

      root.render(
        <CacheProvider value={emotionCache}>
          <QueryClientProvider client={queryClient}>
            <DocumentViewer
              portalRef={portalRef}
              documents={documentsList}
              currentDocument={{ id: documentId, filename }}
              channelName={channelName}
            />
          </QueryClientProvider>
        </CacheProvider>
      );
      win.addEventListener('unload', () => root.unmount());
    };

    renderDocumentViewer();
  };

  return { openDocumentViewer, disconnectDocumentViewer };
};

export default useDocumentViewer;
